<template>
  <div class="annualPlanTarget common_group">
    <!-- 年度规划指标 -->
    <div class="title">
      <img src="@/assets/images/informationIcon.png">
      <span>年度规划指标</span>
    </div>
    <div class="target-table-wrapper">
      <el-table
        :data="annualPlanList"
        border
        size="small"
        :span-method="spanMethod"
        class="target-table"
        :header-cell-style="headerCellStyle"
        :cell-style="bodyCellStyle"
        :key="tableRenderKey"
      >
        <el-table-column
          prop="category"
          label="指标监控"
          width="100"
          align="left"
          class-name="label-cell-column"
        >
          <template #default="{ row }">
            <span :class="{[row.tagKey]: true}">{{ row.category }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="label"
          label=""
          min-width="190"
          align="left"
          class-name="label-cell-column"
        >
          <template #default="{ row }">
            <span :class="{[row.tagKey]: true}">{{ row.label }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="权重（%）"
          min-width="100"
          align="left"
        >
          <template #default="{ row }">
            <el-input-number
              v-if="row.isWeightGroup && !isLoading"
              v-model="row.weightValue"
              controls-position="right"
              @change="() => normalizeGroupWeight(row)"
              @blur="() => normalizeGroupWeight(row)"
              :min="0"
              :max="calcWeightGroupMaxValue(row)"
              :controls="false"
              :precision="2"
              :disabled="isReadonly"
              :class="['input-only-number', { 'is-error': hasWeightError(row) }]"
            />
            <span v-else>--</span>
          </template>
        </el-table-column>
        <el-table-column label="年度" align="left">
          <el-table-column
            label="年度目标"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatDisplay(getAnnualValue(row), row) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            v-if="isCompleted"
            label="系统数据统计"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatDisplay(getAnnualSystem(row), row, row.tagKey) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            v-if="isCompleted"
            label="实际情况"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatDisplay(getAnnualActual(row), row) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            v-if="isCompleted"
            label="完成率（%）"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ isCostCompletionSuppressed(row) ? '--' : formatDisplay(getAnnualCompletion(row), row) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column
          v-for="period in quarterPeriods"
          :key="period.key"
          :label="period.label"
          align="left"
        >
          <el-table-column
            label="季度目标"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <el-input-number
                v-if="row.editable && !isLoading"
                v-model="row.quarters[period.key]"
                controls-position="right"
                @change="() => normalizeQuarter(row, period.key)"
                @blur="() => normalizeQuarter(row, period.key)"
                :min="0"
                :max="row.max"
                :controls="false"
                :precision="row.isInteger ? 0 : 2"
                :disabled="isQuarterDisabled(row, period.key)"
                :class="['input-only-number', { 'is-error': hasQuarterError(row, period.key) }]"
              />
              <!-- resolveQuarterMax(row, period.key) -->
              <span v-else>--</span>
            </template>
          </el-table-column>
          <el-table-column
            v-if="isCompleted"
            label="系统数据统计"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatDisplay(getQuarterSystem(row, period.key), row, row.tagKey) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            v-if="isCompleted"
            label="实际情况"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ formatDisplay(getQuarterActual(row, period.key), row) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            v-if="isCompleted"
            label="完成率（%）"
            min-width="150"
            align="left"
          >
            <template #default="{ row }">
              <span>{{ isCostCompletionSuppressed(row) ? '--' : formatDisplay(getQuarterCompletion(row, period.key), row) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>
<script>
import { allPropsHaveValue, isNotEmpty, roundToDecimals } from '@/utils/utils';
import { INDICATOR_GROUPS, INDICATOR_TYPES, QUARTER_PERIODS } from '@/utils/constants';
const COST_CATEGORY = '成本';
const QUARTER_EDIT_STATUS = [1, 4, 5]; // 可编辑状态值 1-未提交 4-驳回待修改 5-召回待修改
const DEFAULT_DECIMALS = 2; // 默认小数位数

export default {
  name: 'AnnualPlanTarget',
  props: {
    typeVal: {
      type: String,
      default: ''
    },
    editEchoData: {
      type: Object,
      default: () => {}
    },
    isLoading: {
      type: Boolean,
      default: true
    },
    isReadonly: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      rows: [],
      quarterPeriods: QUARTER_PERIODS,
      currentIndicatorType: INDICATOR_TYPES.REGION,
      tableRenderKey: 0,
      isCompletedEdit: 1 // 状态是否是已完成编辑
    };
  },
  computed: {
    annualPlanList () {
      // 按当前指标类型过滤成本类指标
      if (this.currentIndicatorType === INDICATOR_TYPES.INDUSTRY) {
        return this.rows;
      }
      return this.rows.filter(row => row.category !== COST_CATEGORY);
    },
    // 计算当前权重组最大可输入值
    calcWeightGroupMaxValue () {
      return (row) => {
        const currentValue = row.weightValue ? (row.weightValue * 1).toFixed(2) * 1 : 0; // 获取当前权重值
        // 获取表格权重值总和
        const weightValueTotal = this.annualPlanList.reduce((total, item) => {
            const val = item.weightValue ? (item.weightValue * 1).toFixed(2) * 1 : 0;
            return total + val;
          }, 0);
        // 减去表格权重值总和，得到剩余可输入权重值
        const maxVal = 100 - weightValueTotal;
        return maxVal >= 0 ? maxVal + currentValue : 0;
      };
    },
    isCompleted () {
      if (!isNotEmpty(this.editEchoData)) {
        return false;
      }
      const { status } = this.editEchoData;
      const statusList = [2, 6, 7, 8]; // 已完成状态列表
      return statusList.includes(status); // 2-已完成
    },
    // 已完成编辑场景下判断当前表单是否处于只读模式
    completedReadonlyMode () {
      return this.isReadonly && this.isCompletedEdit === 2;
    }
  },
  watch: {
    typeVal (val) {
      const targetType = this.resolveIndicatorType(val);
      if (targetType !== this.currentIndicatorType) {
        this.updateIndicatorRows(targetType);
      }
    },
    editEchoData: {
      handler (val) {
        if (isNotEmpty(val)) {
          this.annualPlanList.forEach(row => {
            // 遍历季度，构造季度目标入参
            // 示例数据结构处理
            // row.quarters = {q1: [], q2: [], q3: [], q4: []};
            QUARTER_PERIODS.forEach(period => {
              row.quarters[period.key] = [];
            });
            // 回显季度实际值 示例数据结构处理
            // row.quarterActual = { q1: undefined, q2: undefined, q3: undefined, q4: undefined };
            if (!row.quarterActual) {
              this.$set(row, 'quarterActual', { q1: undefined, q2: undefined, q3: undefined, q4: undefined });
            }
            // 回显季度系统值 示例数据结构处理
            // row.quarterSystem = { q1: undefined, q2: undefined, q3: undefined, q4: undefined };
            if (!row.quarterSystem) {
              this.$set(row, 'quarterSystem', { q1: undefined, q2: undefined, q3: undefined, q4: undefined });
            }
            // 回显季度状态 示例数据结构处理
            // row.quarterStatus = { q1: undefined, q2: undefined, q3: undefined, q4: undefined };
            if (!row.quarterStatus) {
              this.$set(row, 'quarterStatus', { q1: undefined, q2: undefined, q3: undefined, q4: undefined });
            }
            // 季度是否编辑集合
            if (!row.quarterEditable) {
              this.$set(row, 'quarterEditable', { q1: undefined, q2: undefined, q3: undefined, q4: undefined });
            }
            // 回显季度实际值，将数据初始设置为undefined
            QUARTER_PERIODS.forEach(period => {
              this.$set(row.quarterActual, period.key, undefined);
              this.$set(row.quarterSystem, period.key, undefined);
              this.$set(row.quarterStatus, period.key, undefined);
            });
            // 回显权重值获取weightKey第一个下标当前合并表格有多个相同weightKey取第一个就行
            const index = this.annualPlanList.findIndex(item => item.weightKey === row.weightKey);
            this.annualPlanList[index].weightValue = val[row.weightKey] || 0;
            // 回显季度目标值
            QUARTER_PERIODS.forEach(period => {
              const quarterKey = period.key; // 季度key
              const quarterSource = val[quarterKey] || {};
              Object.entries(quarterSource).forEach(([key, value]) => {
                if (row.tagKey + 'Target' === key) {
                  const quarterValue = value || undefined;
                  row.quarters[quarterKey].push(quarterValue);
                };
              });
            });
            // 回显年度实际与季度实际
            const annualActual = this.resolveAnnualActualValue(val, row);
            this.$set(row, 'annualActual', annualActual);
            // 回显年度系统数据统计
            const annualSystem = this.resolveAnnualSystemValue(val, row);
            this.$set(row, 'annualSystem', annualSystem);
            QUARTER_PERIODS.forEach(period => {
              // 回显季度实际
              const quarterActual = this.resolveQuarterActualValue(val, row, period.key);
              this.$set(row.quarterActual, period.key, quarterActual);
              // 回显季度系统数据统计
              const quarterSystem = this.resolveQuarterSystemValue(val, row, period.key);
              this.$set(row.quarterSystem, period.key, quarterSystem);
              // 回显季度状态
              const periodSource = val[period.key] || {};
              this.$set(row.quarterStatus, period.key, periodSource.status);
              // 设置季度是否可编辑
              this.$set(row.quarterEditable, period.key, periodSource.editable); // 已完成编辑场景下，季度目标不可编辑
            });
            const annualTargetValue = this.toNumber(val[row.key]);
            this.$set(row, 'fixedAnnualTarget', Number.isFinite(annualTargetValue) ? annualTargetValue : undefined);
          });
          this.$nextTick(() => {
            // 触发表格重渲染，避免首次渲染阶段列宽未及时更新导致内容挤压
            this.refreshTable();
          });
        }
      },
      immediate: true,
      deep: true
    }
  },
  created () {
    const initialType = this.resolveIndicatorType(this.typeVal);
    this.currentIndicatorType = initialType;
    this.rows = this.createRows(initialType);
    if (this.$route.query.isCompletedEdit) {
      this.isCompletedEdit = this.$route.query.isCompletedEdit * 1;
    }
  },
  methods: {
    // 解析指标类型
    resolveIndicatorType (val) {
      return val === INDICATOR_TYPES.INDUSTRY ? INDICATOR_TYPES.INDUSTRY : INDICATOR_TYPES.REGION;
    },
    updateIndicatorRows (indicatorType) {
      // 仅按类型重建数量分组，其他分组沿用已填写数据
      const previousMap = this.rows.reduce((acc, row) => {
        acc[row.key] = row;
        return acc;
      }, {});
      this.rows = this.createRows(indicatorType, previousMap);
      this.currentIndicatorType = indicatorType;
      this.refreshTable();
    },
    refreshTable () {
      this.tableRenderKey += 1;
    },
    // 年度规划指标数据校验 ：季度目标和权重值总和是否为100%
    validateHandler () {
      const missingWeightCells = [];
      const weightValues = [];
      const weightGroups = [];
      // 获取指标数据是否填写完整
      const targetIsValue = this.annualPlanList.every(row => allPropsHaveValue(row.quarters));
      // 计算权重值总和
      const weightValueList = this.annualPlanList.filter(item => item.weightValue !== null && item.weightValue !== undefined && item.weightValue !== '').map(item => item.weightValue);
      const weightValueTotal = (weightValueList.reduce((total, val) => total + val, 0).toFixed(2)) * 1;
      // 权重组数量校验
      const currentWeightValueLength = this.typeVal === 'INDUSTRY' ? 4 : 3; // 权重组数量
      // const quartersBalanced = !this.completedReadonlyMode || this.annualPlanList.every(row => this.isQuarterBalanceValid(row));
      // && quartersBalanced
      return new Promise((resolve) => {
        if (targetIsValue && weightValueTotal === 100 && weightValueList.length === currentWeightValueLength) {
          // const hasQuarterSumMismatch = this.annualPlanList.some(row => this.shouldCheckQuarterSum(row) && !this.isQuarterSumAligned(row));
          // if (hasQuarterSumMismatch) {
          //   this.$message.error('季度目标合计必须等于年度目标，请调整季度目标');
          //   resolve(false);
          //   return;
          // }
          resolve(true);
        } else {
          if (weightValueTotal <= 0) {
            // 权重值校验
            this.annualPlanList.forEach(row => {
              if (row.isWeightGroup) {
                weightGroups.push(row);
              }
            });
            weightGroups.forEach(row => {
              if (!Object.prototype.hasOwnProperty.call(row, 'weightError')) {
                this.$set(row, 'weightError', false);
              }
              const weightNumber = this.toNumber(row.weightValue);
              const isValid = Number.isFinite(weightNumber);
              this.$set(row, 'weightError', !isValid);
              if (!isValid) {
                missingWeightCells.push(this.formatWeightMissingLabel(row));
              } else {
                weightValues.push(weightNumber);
              }
            });
            this.$message.error('请输入权重值');
            return false;
          }
          if (weightValueTotal !== 100) {
            this.$message.error('权重值总和必须为100%');
            return false;
          }
          if (!targetIsValue) {
            // 季度目标值校验
            const missingQuarterCells = [];
            this.annualPlanList.forEach(row => {
              if (!row.editable || this.isReadonly) {
                return;
              }
              if (!row.errors) {
                this.$set(row, 'errors', { q1: false, q2: false, q3: false, q4: false });
              }
              this.quarterPeriods.forEach(period => {
                const value = this.toNumber(row.quarters[period.key]);
                const isValid = Number.isFinite(value);
                this.$set(row.errors, period.key, !isValid);
                if (!isValid) {
                  missingQuarterCells.push(this.formatQuarterMissingLabel(row, period));
                }
              });
            });
            this.$message.error('请输入季度目标值');
            return false;
          }
          // if (!quartersBalanced) {
          //   const hasCostImbalance = this.annualPlanList.some(row => row.category === COST_CATEGORY && !this.isQuarterBalanceValid(row));
          //   this.$message.error(hasCostImbalance ? '成本类指标季度目标合计必须等于年度目标×4，请调整季度目标' : '可编辑季度目标合计必须等于年度目标，请调整季度目标');
          //   return false;
          // }
          // const hasQuarterSumMismatch = this.annualPlanList.some(row => this.shouldCheckQuarterSum(row) && !this.isQuarterSumAligned(row));
          // if (hasQuarterSumMismatch) {
          //   this.$message.error('季度目标合计必须等于年度目标，请调整季度目标');
          //   return false;
          // }
          resolve(false);
        }
      });
    },
    // 获取年度规划指标入参
    getAnnualParamsValue () {
      const params = {};
      // 遍历季度，构造季度目标入参
      QUARTER_PERIODS.forEach(period => {
        params[period.key + 'Target'] = {};
      });
      if (isNotEmpty(this.annualPlanList)) {
        if (this.$route.query.isCompletedEdit === '2') {
          params.quarterEditable = this.annualPlanList[0].quarterEditable;
        }
        this.annualPlanList.forEach(row => {
          // 设置权重值
          if (!params[row.weightKey] && row.weightValue) {
            params[row.weightKey] = row.weightValue; // 权重值
          }
          // 计算当前row年度目标值
          const annualValue = this.getAnnualValue(row);
          params[row.key] = annualValue; // 年度目标值

          Object.entries(row.quarters).forEach(([key, value]) => {
            params[key + 'Target'][row.tagKey + 'Quarter'] = this.toNumber(value);
          }); // 遍历季度目标设置入参格式化
        });
      }
      return params;
    },
    // 创建表格行数据
    createRows (indicatorType, previousMap = {}) {
      const rows = [];
      INDICATOR_GROUPS.forEach(group => {
        const groupRows = this.resolveGroupRows(group, indicatorType);
        groupRows.forEach((item, index) => {
          const rowKey = `${item.key}Annual`;
          const previous = previousMap[rowKey];
          rows.push(this.buildRowState({
            definition: item,
            group,
            index,
            total: groupRows.length,
            previous
          }));
        });
      });
      return rows;
    },
    // 按类型裁剪指标行配置
    resolveGroupRows (group, indicatorType) {
      const definitions = Array.isArray(group.rows) ? group.rows : [];
      const filtered = definitions.filter(item => {
        if (!Array.isArray(item.types) || item.types.length === 0) {
          return true;
        }
        return item.types.includes(indicatorType);
      });
      const orderCache = new Map();
      definitions.forEach((item, idx) => {
        orderCache.set(item.key, idx);
      });
      return filtered.sort((a, b) => {
        const orderA = this.resolveRowOrder(a, indicatorType, orderCache.get(a.key));
        const orderB = this.resolveRowOrder(b, indicatorType, orderCache.get(b.key));
        return orderA - orderB;
      });
    },
    // 解析行排序索引
    resolveRowOrder (definition, indicatorType, fallbackIndex = 0) {
      if (
        definition &&
        definition.typeOrder &&
        Object.prototype.hasOwnProperty.call(definition.typeOrder, indicatorType)
      ) {
        return definition.typeOrder[indicatorType];
      }
      return Number.isFinite(fallbackIndex) ? fallbackIndex : 0;
    },
    // 构建表格行状态
    buildRowState ({ definition, group, index, total, previous }) {
      const quarters = previous
        ? { ...previous.quarters }
        : {
            q1: undefined,
            q2: undefined,
            q3: undefined,
            q4: undefined
          };
      const quarterActual = previous && previous.quarterActual
        ? { ...previous.quarterActual }
        : {
            q1: undefined,
            q2: undefined,
            q3: undefined,
            q4: undefined
          };
      const quarterSystem = previous && previous.quarterSystem
        ? { ...previous.quarterSystem }
        : {
            q1: undefined,
            q2: undefined,
            q3: undefined,
            q4: undefined
          };
      return {
        key: `${definition.key}Annual`,
        tagKey: definition.key,
        category: group.category,
        label: definition.label,
        calc: definition.calc,
        decimals: definition.decimals,
        isInteger: definition.isInteger,
        max: definition.max,
        isWeightGroup: index === 0,
        editable: previous ? previous.editable : true,
        groupSpan: index === 0 ? total : 0,
        quarters,
        quarterActual,
        quarterSystem,
        errors: previous && previous.errors
          ? { ...previous.errors }
          : {
              q1: false,
              q2: false,
              q3: false,
              q4: false
            },
        weightError: previous ? previous.weightError : false,
        weightKey: group.weightKey,
        weightValue: previous ? previous.weightValue : undefined,
        annualActual: previous ? previous.annualActual : undefined,
        annualSystem: previous ? previous.annualSystem : undefined
      };
    },
    // 解析年度实际值，兼容年度规划与季度填报返回的多种字段命名
    resolveAnnualActualValue (source, row) {
      const keyBase = row.tagKey;
      const annualKey = row.key;
      const candidateValues = [];
      const annualIndicators = source && source.annualIndicators;
      if (annualIndicators && typeof annualIndicators === 'object') {
        candidateValues.push(annualIndicators[`${keyBase}Actual`]);
        candidateValues.push(annualIndicators[`${annualKey}Actual`]);
        candidateValues.push(annualIndicators[`${keyBase}AnnualActual`]);
        candidateValues.push(annualIndicators[`${annualKey}AnnualActual`]);
      }
      candidateValues.push(source[`${keyBase}Actual`]);
      candidateValues.push(source[`${annualKey}Actual`]);
      candidateValues.push(source[`${keyBase}AnnualActual`]);
      candidateValues.push(source[`${annualKey}AnnualActual`]);
      return this.normalizeActualValue(candidateValues, row.decimals, row.isInteger);
    },
    // 解析季度实际值，兼容对象嵌套与扁平结构
    resolveQuarterActualValue (source, row, quarterKey) {
      const keyBase = row.tagKey;
      const candidateValues = [];
      const quarterIndicators = source && source.quarterIndicators;
      const quarterActualKey = `${quarterKey}Actual`;
      const collectFromMap = (map, includePlainKey = false) => {
        if (!map || typeof map !== 'object') {
          return;
        }
        const keys = [
          `${keyBase}Actual`,
          `${row.key}Actual`,
          `${keyBase}QuarterActual`,
          `${row.key}QuarterActual`
        ];
        if (includePlainKey) {
          keys.push(keyBase, row.key);
        }
        keys.forEach(itemKey => {
          if (Object.prototype.hasOwnProperty.call(map, itemKey)) {
            candidateValues.push(map[itemKey]);
          }
        });
      };
      collectFromMap(quarterIndicators && quarterIndicators[quarterActualKey], true);
      collectFromMap(quarterIndicators && quarterIndicators[quarterKey]);
      collectFromMap(source[quarterActualKey], true);
      collectFromMap(source[quarterKey]);
      candidateValues.push(source[`${keyBase}${quarterKey.toUpperCase()}Actual`]);
      candidateValues.push(source[`${keyBase}${quarterKey.toUpperCase()}`]);
      return this.normalizeActualValue(candidateValues, row.decimals, row.isInteger);
    },
    // 解析年度系统数据
    resolveAnnualSystemValue (source, row) {
      const keyBase = row.tagKey;
      const annualKey = row.key;
      const candidateValues = [];
      const names = [
        `${keyBase}System`,
        `${annualKey}System`
      ];
      const containers = [ source ];
      const collectValues = (container) => {
        if (container === undefined || container === null) {
          return;
        }
        names.forEach(name => {
          if (Object.prototype.hasOwnProperty.call(container, name)) {
            candidateValues.push(container[name]);
          }
        });
      };
      containers.forEach(collectValues);
      return this.pickFirstPresentValue(candidateValues);
    },
    // 解析季度系统数据
    resolveQuarterSystemValue (source, row, quarterKey) {
      const keyBase = row.tagKey;
      const candidateValues = [];
      const quarterIndicators = source && source.quarterIndicators;
      const quarterActualKey = `${quarterKey}Actual`;
      const collectFromMap = (map, includePlainKey = false) => {
        if (!map || typeof map !== 'object') {
          return;
        }
        const keys = [
          `${keyBase}System`,
          `${row.key}System`,
          `${keyBase}QuarterSystem`,
          `${row.key}QuarterSystem`
        ];
        if (includePlainKey) {
          keys.push(keyBase, row.key);
        }
        keys.forEach(itemKey => {
          if (Object.prototype.hasOwnProperty.call(map, itemKey)) {
            candidateValues.push(map[itemKey]);
          }
        });
      };
      collectFromMap(quarterIndicators && quarterIndicators[quarterActualKey], true);
      collectFromMap(quarterIndicators && quarterIndicators[quarterKey]);
      collectFromMap(source[quarterActualKey], true);
      collectFromMap(source[quarterKey]);
      if (candidateValues.length > 0) {
        const number = this.toNumber(candidateValues[0]);
        if (row.tagKey === 'cpsql' && candidateValues.length > 0 && typeof candidateValues[0] === 'string' && candidateValues[0].trim() !== '' && !Number.isFinite(number)) {
          return candidateValues[0];
        }
        return this.normalizeActualValue(candidateValues, row.decimals, row.isInteger);
      } else {
        return undefined;
      }
    },
    // 规范化原始实际值，返回首个有效数字
    normalizeActualValue (candidates, decimals, isInteger) {
      const precision = isInteger ? 0 : (Number.isFinite(decimals) ? decimals : 2);
      const items = Array.isArray(candidates) ? candidates : [candidates];
      for (let i = 0; i < items.length; i += 1) {
        const number = this.toNumber(items[i]);
        if (Number.isFinite(number)) {
          return this.fixPrecision(number, precision);
        }
      }
      return undefined;
    },
    // 计算年度实际值：优先使用存量值，缺失时按季度实际累积
    getAnnualActual (row) {
      if (!row) {
        return undefined;
      }
      const stored = this.normalizeActualValue([row.annualActual], row.decimals, row.isInteger);
      if (stored !== undefined) {
        return stored;
      }
      return this.calcActualAggregate(row);
    },
    // 获取季度实际值，保持数值精度
    getQuarterActual (row, quarterKey) {
      if (!row || !row.quarterActual) {
        return undefined;
      }
      return this.normalizeActualValue([row.quarterActual[quarterKey]], row.decimals, row.isInteger);
    },
    // 获取年度系统统计值
    getAnnualSystem (row) {
      if (!row) {
        return undefined;
      }
      return row.annualSystem;
    },
    // 获取季度系统统计值
    getQuarterSystem (row, quarterKey) {
      if (!row || !row.quarterSystem) {
        return undefined;
      }
      return row.quarterSystem[quarterKey];
    },
    // 基于季度实际计算年度汇总值
    calcActualAggregate (row) {
      if (!row || !row.quarterActual) {
        return undefined;
      }
      const numbers = this.quarterPeriods.map(item => this.toNumber(row.quarterActual[item.key])).filter(val => Number.isFinite(val));
      if (!numbers.length) {
        return undefined;
      }
      const total = numbers.reduce((sum, num) => sum + num, 0);
      const result = row.calc === 'average' ? total / numbers.length : total;
      return this.fixPrecision(result, row.decimals);
    },
    // 计算年度完成率
    getAnnualCompletion (row) {
      if (this.isCostCompletionSuppressed(row)) {
        return undefined;
      }
      const actual = this.toNumber(this.getAnnualActual(row));
      const target = this.toNumber(this.getAnnualValue(row));
      if (!Number.isFinite(actual) || !Number.isFinite(target) || target === 0) {
        return undefined;
      }
      return this.fixPrecision((actual / target) * 100, 2);
    },
    // 计算季度完成率
    getQuarterCompletion (row, quarterKey) {
      if (this.isCostCompletionSuppressed(row)) {
        return undefined;
      }
      const actual = this.toNumber(this.getQuarterActual(row, quarterKey));
      const target = this.toNumber(row.quarters ? row.quarters[quarterKey] : undefined);
      if (!Number.isFinite(actual) || !Number.isFinite(target) || target === 0) {
        return undefined;
      }
      return this.fixPrecision((actual / target) * 100, 2);
    },
    // 是否禁用成本完成率显示逻辑
    isCostCompletionSuppressed (row) {
      return this.currentIndicatorType === INDICATOR_TYPES.INDUSTRY && row && row.category === COST_CATEGORY;
    },
    // 合并单元格方法
    spanMethod ({ row, column, columnIndex }) {
      if (columnIndex === 0 || columnIndex === 2) {
        return {
          rowspan: row.groupSpan,
          colspan: row.groupSpan === 0 ? 0 : 1
        };
      }
      return { rowspan: 1, colspan: 1 };
    },
    // 表头单元格样式
    headerCellStyle () {
      return {
        background: '#f6f7f9',
        color: '#424C57',
        borderColor: '#e2e4e9',
        fontWeight: 'normal'
      };
    },
    // 表体单元格样式
    bodyCellStyle () {
      return {
        color: '#424C57',
        borderColor: '#e2e4e9',
        padding: '6.5px 8px'
      };
    },
    // 判断季度审批状态是否处于可编辑态
    isPendingQuarterStatus (status) {
      return QUARTER_EDIT_STATUS.includes(status);
    },
    // 判定已完成流程下当前行指定季度是否允许二次填写
    canEditCompletedQuarter (row, period) {
      if (!row || !this.completedReadonlyMode) {
        return false;
      }
      const quarterStatus = row.quarterStatus ? row.quarterStatus[period] : undefined;
      return this.isPendingQuarterStatus(quarterStatus);
    },
    // 计算季度输入框的禁用状态
    isQuarterDisabled (row, period) {
      if (!row || !row.editable) {
        return true;
      }
      if (!this.isReadonly) {
        return false;
      }
      if (!this.completedReadonlyMode) {
        return true;
      }
      return !this.canEditCompletedQuarter(row, period);
    },
    // 计算季度输入的最大可填写值
    resolveQuarterMax (row, period) {
      const baseMax = Number.isFinite(row.max) ? row.max : undefined;
      if (!this.completedReadonlyMode || !this.canEditCompletedQuarter(row, period)) {
        return baseMax;
      }
      const allowance = this.calcQuarterAllowance(row, period);
      if (!Number.isFinite(allowance)) {
        return baseMax;
      }
      if (!Number.isFinite(baseMax)) {
        return allowance;
      }
      return Math.min(baseMax, allowance);
    },
    // 计算在年度目标约束下当前季度可再分配的额度
    calcQuarterAllowance (row, excludeKey) {
      if (!this.completedReadonlyMode) {
        return undefined;
      }
      const editableTotal = this.calcEditableQuarterTotal(row);
      if (!Number.isFinite(editableTotal)) {
        return undefined;
      }
      const otherEditableSum = this.calcEditableQuarterSum(row, excludeKey);
      const decimals = Number.isFinite(row.decimals) ? row.decimals : DEFAULT_DECIMALS;
      const allowance = this.fixPrecision(editableTotal - otherEditableSum, decimals);
      return allowance < 0 ? 0 : allowance;
    },
    // 汇总不可编辑季度的目标值
    calcLockedQuarterSum (row) {
      if (!row || !row.quarters) {
        return 0;
      }
      return this.quarterPeriods.reduce((sum, period) => {
        if (this.canEditCompletedQuarter(row, period.key)) {
          return sum;
        }
        const value = this.toNumber(row.quarters[period.key]);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
    },
    // 汇总其余可编辑季度的目标值
    calcEditableQuarterSum (row, excludeKey) {
      if (!row || !row.quarters) {
        return 0;
      }
      return this.quarterPeriods.reduce((sum, period) => {
        if (excludeKey && period.key === excludeKey) {
          return sum;
        }
        if (!this.canEditCompletedQuarter(row, period.key)) {
          return sum;
        }
        const value = this.toNumber(row.quarters[period.key]);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
    },
    // 计算可分配季度总额度
    calcEditableQuarterTotal (row) {
      if (!this.completedReadonlyMode) {
        return undefined;
      }
      const annualTargetTotal = this.resolveAnnualTargetTotal(row);
      if (!Number.isFinite(annualTargetTotal)) {
        return undefined;
      }
      const lockedSum = this.calcLockedQuarterSum(row);
      const decimals = Number.isFinite(row.decimals) ? row.decimals : DEFAULT_DECIMALS;
      const total = this.fixPrecision(annualTargetTotal - lockedSum, decimals);
      return total < 0 ? 0 : total;
    },
    // 计算单行季度目标总和
    calcQuarterTotal (row) {
      if (!row || !row.quarters) {
        return NaN;
      }
      const total = this.quarterPeriods.reduce((sum, period) => {
        const value = this.toNumber(row.quarters[period.key]);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
      return total;
    },
    // 判断是否需要执行季度合计校验
    shouldCheckQuarterSum (row) {
      if (!row || row.calc === 'average') {
        return false;
      }
      // 非平均类指标在任何场景都要求季度合计等于年度目标
      return true;
    },
    // 判断季度合计是否与年度目标匹配
    isQuarterSumAligned (row) {
      const annualTarget = this.resolveAnnualFixedTarget(row);
      if (!Number.isFinite(annualTarget)) {
        return true;
      }
      const total = this.calcQuarterTotal(row);
      if (!Number.isFinite(total)) {
        return true;
      }
      return this.isValueEqual(total, annualTarget, row.decimals);
    },
    // 获取年度目标的固定值，优先回显缓存数据
    resolveAnnualFixedTarget (row) {
      if (!row) {
        return undefined;
      }
      const stored = this.toNumber(row.fixedAnnualTarget);
      if (Number.isFinite(stored)) {
        return stored;
      }
      const source = this.editEchoData ? this.toNumber(this.editEchoData[row.key]) : NaN;
      if (Number.isFinite(source)) {
        this.$set(row, 'fixedAnnualTarget', source);
        return source;
      }
      return undefined;
    },
    // 计算年度目标的总额度（平均类指标需乘以季度数）
    resolveAnnualTargetTotal (row) {
      const annualTarget = this.resolveAnnualFixedTarget(row);
      if (!Number.isFinite(annualTarget)) {
        return undefined;
      }
      const multiplier = row && row.calc === 'average' ? this.quarterPeriods.length : 1;
      const decimals = Number.isFinite(row.decimals) ? row.decimals : DEFAULT_DECIMALS;
      return this.fixPrecision(annualTarget * multiplier, decimals);
    },
    // 校验当前行季度值合计是否等于年度目标
    isQuarterBalanceValid (row) {
      if (!this.completedReadonlyMode) {
        return true;
      }
      const annualTargetTotal = this.resolveAnnualTargetTotal(row);
      if (!Number.isFinite(annualTargetTotal)) {
        return true;
      }
      const total = this.calcLockedQuarterSum(row) + this.calcEditableQuarterSum(row);
      return this.isValueEqual(total, annualTargetTotal, row.decimals);
    },
    // 按指定精度判断两个数值是否相等
    isValueEqual (valueA, valueB, decimals = DEFAULT_DECIMALS) {
      if (!Number.isFinite(valueA) || !Number.isFinite(valueB)) {
        return false;
      }
      const precision = Number.isFinite(decimals) ? decimals : DEFAULT_DECIMALS;
      const tolerance = 1 / Math.pow(10, precision);
      return Math.abs(valueA - valueB) <= tolerance;
    },
    // 季度目标值归一化
    normalizeQuarter (row, period) {
      const val = this.toNumber(row.quarters[period]);
      if (!Number.isFinite(val)) {
        this.$set(row.quarters, period, undefined);
        if (row.errors) {
          this.$set(row.errors, period, true);
        }
        return;
      }
      const fixed = this.fixPrecision(val, row.decimals);
      this.$set(row.quarters, period, fixed);
      if (row.errors) {
        this.$set(row.errors, period, false);
      }
    },
    // 季度目标值归一化计算
    normalizeQuarterCalc (row, period) {
      const val = this.toNumber(row.quarters[period]);
      if (!Number.isFinite(val)) {
        this.$set(row.quarters, period, undefined);
        if (row.errors) {
          this.$set(row.errors, period, true);
        }
        return;
      }
      const fixed = this.fixPrecision(val, row.decimals);
      let normalized = fixed;
      if (this.completedReadonlyMode && this.canEditCompletedQuarter(row, period)) {
        if (row && row.calc === 'average') {
          const editableTotal = this.calcEditableQuarterTotal(row);
          if (Number.isFinite(editableTotal) && normalized > editableTotal) {
            normalized = this.fixPrecision(editableTotal, row.decimals);
            this.$message.warning(`当前季度可填写的最大值为 ${normalized}`);
          }
        } else {
          const maxAllowed = this.calcQuarterAllowance(row, period);
          if (Number.isFinite(maxAllowed) && normalized > maxAllowed) {
            normalized = this.fixPrecision(Math.max(maxAllowed, 0), row.decimals);
            this.$message.warning(`当前季度可填写的最大值为 ${normalized}`);
          }
        }
      }
      this.$set(row.quarters, period, normalized);
      if (row.errors) {
        this.$set(row.errors, period, false);
      }
      if (this.completedReadonlyMode && this.canEditCompletedQuarter(row, period) && row && row.calc === 'average') {
        this.rebalanceEditableQuarters(row, period);
      }
    },
    // 权重值归一化
    normalizeGroupWeight (row) {
      let val = this.toNumber(row.weightValue);
      if (!Number.isFinite(val)) {
        row.weightValue = undefined;
        if (Object.prototype.hasOwnProperty.call(row, 'weightError')) {
          this.$set(row, 'weightError', true);
        }
        return;
      }
      val = Math.min(Math.max(val, 0), 100);
      row.weightValue = this.fixPrecision(val, 2);
      if (Object.prototype.hasOwnProperty.call(row, 'weightError')) {
        this.$set(row, 'weightError', false);
      }
    },
    // 调整余下季度以满足年度目标守恒
    rebalanceEditableQuarters (row, changedKey) {
      if (!row || row.calc !== 'average') {
        return;
      }
      const editableTotal = this.calcEditableQuarterTotal(row);
      if (!Number.isFinite(editableTotal)) {
        return;
      }
      const editableKeys = this.quarterPeriods
        .map(period => period.key)
        .filter(key => this.canEditCompletedQuarter(row, key));
      if (!editableKeys.length) {
        return;
      }
      const sumEditable = editableKeys.reduce((sum, key) => {
        const value = this.toNumber(row.quarters[key]);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
      if (sumEditable <= editableTotal) {
        return;
      }
      const precision = Number.isFinite(row.decimals) ? row.decimals : DEFAULT_DECIMALS;
      const tolerance = 1 / Math.pow(10, precision);
      let remaining = editableTotal;
      const changedValue = this.toNumber(row.quarters[changedKey]);
      let normalizedChanged = Number.isFinite(changedValue) ? changedValue : 0;
      if (normalizedChanged > editableTotal) {
        normalizedChanged = this.fixPrecision(editableTotal, row.decimals);
        this.$set(row.quarters, changedKey, normalizedChanged);
      }
      remaining -= normalizedChanged;
      if (remaining < 0) {
        remaining = 0;
      }
      editableKeys.forEach(key => {
        if (key === changedKey) {
          return;
        }
        const current = this.toNumber(row.quarters[key]);
        if (!Number.isFinite(current)) {
          this.$set(row.quarters, key, undefined);
          return;
        }
        if (current <= remaining + tolerance) {
          remaining -= current;
          return;
        }
        const adjusted = this.fixPrecision(Math.max(remaining, 0), row.decimals);
        this.$set(row.quarters, key, adjusted);
        remaining -= adjusted;
      });
    },
    // 计算年度目标值
    getAnnualValue (row) {
      if (this.completedReadonlyMode) {
        const fixedTarget = this.resolveAnnualFixedTarget(row);
        if (Number.isFinite(fixedTarget)) {
          return this.fixPrecision(fixedTarget, row.decimals);
        }
      }
      const numbers = this.quarterPeriods.map(item => this.toNumber(row.quarters[item.key])).filter(val => Number.isFinite(val));
      if (numbers.length === 0) {
        return null;
      }
      if (row.calc === 'average') {
        const avg = numbers.reduce((sum, num) => sum + num, 0) / numbers.length;
        return this.fixPrecision(avg, row.decimals);
      }
      const sum = numbers.reduce((total, num) => total + num, 0);
      return this.fixPrecision(sum, row.decimals);
    },
    // 辅助函数：转换为数字
    toNumber (value) {
      if (value === null || value === undefined || value === '') {
        return NaN;
      }
      const number = Number(value);
      return Number.isNaN(number) ? NaN : number;
    },
    // 辅助函数：修正数值精度
    fixPrecision (value, decimals = 0) {
      return roundToDecimals(value, decimals);
    },
    // 辅助函数：格式化显示值
    formatDisplay (value, row = {}, tagKey = '') {
      const isCurrency = typeof row.label === 'string' && row.label.includes('（元）');
      const decimals = Number.isFinite(row.decimals) ? row.decimals : 2;
      const numericValue = this.toNumber(value);
      const isFiniteNumber = Number.isFinite(numericValue);
      if (tagKey === 'cpsql' && typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== '' && !Number.isFinite(numericValue)) {
          return trimmed;
        }
      }
      if (!isFiniteNumber) {
        return isCurrency ? (0).toFixed(decimals) : 0;
      }
      if (isCurrency) {
        return numericValue.toFixed(decimals);
      }
      return numericValue;
    },
    pickFirstPresentValue (candidates) {
      const items = Array.isArray(candidates) ? candidates : [candidates];
      for (let i = 0; i < items.length; i += 1) {
        const current = items[i];
        if (current !== undefined && current !== null && current !== '') {
          return current;
        }
      }
      return undefined;
    },
    hasQuarterError (row, period) {
      if (!row || !row.errors) {
        return false;
      }
      return Boolean(row.errors[period]);
    },
    hasWeightError (row) {
      if (!row) {
        return false;
      }
      return Boolean(row.weightError);
    },
    formatQuarterMissingLabel (row, period) {
      const category = typeof row.category === 'string' ? row.category : '';
      const label = typeof row.label === 'string' ? row.label : '';
      const periodLabel = period && period.label ? period.label : '';
      return [category, label, periodLabel].filter(Boolean).join('-') || periodLabel || '季度目标';
    },
    formatWeightMissingLabel (row) {
      const category = typeof row.category === 'string' ? row.category : '';
      const label = typeof row.label === 'string' ? row.label : '';
      return [category, label].filter(Boolean).join('-') || label || category || '权重';
    }
  }
};
</script>
<style lang="scss" scoped>
.annualPlanTarget {
  .target-table-wrapper {
    margin-top: 16px;
  }
  .target-table {
    width: 100%;
    border-color: #e2e4e9;
    ::v-deep .el-table__header tr:first-child th:first-child{
      border-right: 0;
    }
    ::v-deep tbody tr:hover>td {
      background-color: transparent;
    }
    ::v-deep .el-table__body-wrapper {
      &::-webkit-scrollbar {
        height: 8px;
        width: 8px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 5px;
        background: rgba(144, 147, 153, 0.3);
      }
      &::-webkit-scrollbar-thumb:hover {
        background: rgba(144, 147, 153, 0.5);
      }
      &::-webkit-scrollbar-track {
        border-radius: 10px;
        background: #ffffff;
      }
    }
    ::v-deep .is-disabled > input {
      background: transparent;
      border: none;
      color: #424C57;
      cursor: default;
      font-size: 12px;
      text-align: left;
    }
    ::v-deep tbody tr .label-cell-column {
      background: #f6f7f9;
    }
    ::v-deep tbody tr:hover>td.label-cell-column {
      background: #f6f7f9;
    }
  }
  .input-only-number{
    width: 100%;
    ::v-deep .el-input{
      height: 100%;
      input{
        height: 34px;
        padding: 0;
        font-size: 12px;
        color: #424C57;
      }
    }
    &.is-error {
      ::v-deep .el-input__inner {
        border-color: #F56C6C;
      }
    }
  }
  .tips {
    margin-top: 8px;
    color: #F56C6C;
    font-size: 12px;
  }
}
</style>
